import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../api/client';
import type { PlanDefinition, PlansResponse } from '../api/types';
import { Nettstedsbunn, Nettstedstopp, useHovedvei } from '../components/Nettsted';
import { PlusProveTilbud, PlusProveVilkaar } from '../components/Proveperiode';
import { formatNok } from '../lib/format';
import {
  FUNKSJONER,
  I_ALLE_PAKKENE,
  NAVN_PAA_FUNKSJON,
  planIncludes,
  provelengde,
  requiredPlanFor,
} from '../lib/plans';
import { prislinje } from '../lib/selskap';
import { SIDETITLER, useSidehode } from '../lib/sidehode';
import { meldLanding } from '../lib/trakt';
import '../styles/landing.css';

/**
 * Prislista på egen adresse.
 *
 * Sto tidligere bare som en bolk på forsiden, nåbar gjennom `#priser`. Det
 * holder for den som allerede er på forsiden og ruller nedover, og for ingen
 * andre: et anker kan ikke deles i en melding uten at mottakeren først får se
 * hele forsiden, det kan ikke rangeres for seg i et søk, og en forhåndsvisning
 * viser forsidens tittel uansett hvor i dokumentet lenka peker.
 *
 * Prisen er som regel det andre folk vil vite – rett etter hva tjenesten er –
 * og det ene spørsmålet de sender videre til den de skal gifte seg med. Da må
 * svaret ha en adresse.
 *
 * Forsiden har fortsatt en kort prisbolk. Den er ikke det samme innholdet:
 * der står de tre tallene, her står hva som faktisk skiller pakkene, hva som
 * skjer når man betaler, og hva som skjer etterpå.
 */

/** Spørsmålene som ellers kommer på e-post. Svarene skal være til å ta stilling til. */
const SPORSMAL: { sporsmal: string; svar: React.ReactNode }[] = [
  {
    sporsmal: 'Er dette et abonnement?',
    svar: (
      <>
        Nei, dere betaler én gang for hele bryllupet. Dere kan bruke Vår Dag i 6 måneder
        etter bryllupet. Ingen automatisk fornyelse.
      </>
    ),
  },
  {
    sporsmal: 'Kan vi prøve Plus før vi bestemmer oss?',
    svar: (
      <>
        Ja. Alle som registrerer seg, får Plus gratis i {provelengde()}, regnet fra dagen
        kontoen opprettes – enten dere begynner på Gratis eller kjøper Standard med det
        samme. Noen dager før perioden er over, får dere beskjed på e-post og i appen. Ingenting
        trekkes av seg selv, og ingenting dere har lagt inn, blir slettet når perioden er over.
      </>
    ),
  },
  {
    sporsmal: 'Kan vi begynne gratis og betale senere?',
    svar: (
      <>
        Ja! Begynn med gjesteliste og invitasjoner gratis, og oppgrader når dere trenger
        flere verktøy. Alt dere har lagt inn, blir med videre.
      </>
    ),
  },
  {
    sporsmal: 'Hva koster det å gå fra Standard til Plus?',
    svar: (
      <>
        Mellomlegget, ikke full pris. Har dere betalt 590 for Standard, koster Plus 400 til.
        Dere betaler aldri to ganger for det samme.
      </>
    ),
  },
  {
    sporsmal: 'Kan vi angre?',
    svar: (
      <>
        Ja. Dere har fjorten dagers angrerett, selv om tilgangen åpnes med én gang. Se{' '}
        <Link to="/salgsbetingelser">salgsbetingelsene</Link> for hvordan dere går fram.
      </>
    ),
  },
  {
    sporsmal: 'Hva skjer med gjestelista etter bryllupet?',
    svar: (
      <>
        Dere kommer til den så lenge kontoen er aktiv, og kan slette alt selv når dere vil.
        Hva vi lagrer og hvor lenge, står i{' '}
        <Link to="/personvern">personvernerklæringen</Link>.
      </>
    ),
  },
  {
    sporsmal: 'Må gjestene lage konto?',
    svar: (
      <>
        Nei. Hver gjest får sin egen svarlenke og svarer på ett skjema. De trenger verken
        konto, passord eller app.
      </>
    ),
  },
];

export function PrisPage() {
  const [pakker, setPakker] = useState<PlansResponse | null>(null);
  const [feilet, setFeilet] = useState(false);
  const { href, tekst, innlogget } = useHovedvei();

  useSidehode(SIDETITLER.priser, '/priser');

  // Prislista er en egen inngang til nettstedet, og teller i det samme første
  // steget som forsiden. Kommer man hit fra forsiden, er det allerede meldt.
  useEffect(() => {
    meldLanding('priser');
  }, []);

  useEffect(() => {
    api
      .get<PlansResponse>('/billing/plans')
      .then(setPakker)
      .catch(() => setFeilet(true));
  }, []);

  return (
    <>
      <Nettstedstopp />

      <main>
        <section className="section prisside-topp">
          <div className="container">
            <div className="section-head center">
              <h1>Én pris for hele bryllupet</h1>
              <p>
                Betal én gang for hele bryllupet, uansett antall gjester. Dere kan bruke
                Vår Dag i 6 måneder etter den store dagen.
              </p>
            </div>

            {innlogget ? null : <PlusProveTilbud />}

            {pakker ? (
              <div className="pricing">
                {pakker.plans.map((pakke) => (
                  <Pakkekort
                    key={pakke.id}
                    pakke={pakke}
                    kjopsvei={innlogget ? '/app/abonnement' : '/registrer'}
                    registrert={innlogget}
                  />
                ))}
              </div>
            ) : feilet ? (
              /*
                Prisene hentes fra API-et, som er den som bestemmer dem. Svarer
                det ikke, står det her hva som er galt – å skrive tallene inn i
                sida som reserve ville betydd at en feil viste en pris ingen
                lenger tar.
              */
              <p className="center muted">
                Vi fikk ikke hentet prisene akkurat nå. Prøv å laste siden på nytt, eller{' '}
                <a href="mailto:post@vardag.no">send oss en e-post</a>.
              </p>
            ) : (
              <p className="center muted">Henter priser …</p>
            )}

            {/* Det boksen over ikke lenger forklarer: at tilbudet gjelder
                uansett hvilket kort man velger, og at ingenting trekkes. */}
            {innlogget ? null : <PlusProveVilkaar />}

            <p className="center small muted prisside-fotnote">
              Betal med Vipps eller kort. Kjøpet er et engangsbeløp for ett bryllup – se{' '}
              <Link to="/salgsbetingelser">salgsbetingelsene</Link>.
            </p>
          </div>
        </section>

        <section className="section section-alt">
          <div className="container">
            <div className="section-head center">
              <h2>Finn pakken som passer dere</h2>
            </div>
            <Sammenligning pakker={pakker?.plans ?? null} />
          </div>
        </section>

        <section className="section">
          <div className="container prisside-smal">
            <div className="section-head">
              <h2>Spørsmål vi får</h2>
            </div>
            <dl className="prisside-svar">
              {SPORSMAL.map((rad) => (
                <div key={rad.sporsmal}>
                  <dt>{rad.sporsmal}</dt>
                  <dd>{rad.svar}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        <section className="section" style={{ paddingTop: 0 }}>
          <div className="container">
            <div className="cta-band">
              <h2>Prøv før dere bestemmer dere</h2>
              <p>
                Opprett bryllupet, legg inn de første gjestene og send ut svarlenkene. Det
                koster ingenting, og dere kan oppgradere når som helst.
              </p>
              <Link className="btn btn-primary btn-lg" to={href}>
                {tekst}
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Nettstedsbunn />
    </>
  );
}

/**
 * @param registrert Om den som leser, allerede har konto. Avgjør ordet på
 *   knappen: den som ikke har begynt ennå, *velger* en pakke, og den som har
 *   det, *kjøper* den. «Velg Plus» til et par som har vært kunde i et halvt år,
 *   later som om spørsmålet fortsatt er hvilken pakke de skal ha.
 */
function Pakkekort({
  pakke,
  kjopsvei,
  registrert,
}: {
  pakke: PlanDefinition;
  kjopsvei: string;
  registrert: boolean;
}) {
  return (
    <article className={`plan-card${pakke.recommended ? ' recommended' : ''}`}>
      {pakke.recommended ? <span className="plan-flag">Mest valgt</span> : null}
      <h3 className="plan-name">{pakke.name}</h3>
      <p className="plan-tagline">{pakke.tagline}</p>
      <div className="plan-price">
        {pakke.priceNok === 0 ? 'Gratis' : formatNok(pakke.priceNok)}
      </div>
      <div className="small muted">{pakke.priceNok === 0 ? 'For alltid' : prislinje()}</div>
      <ul className="plan-list">
        {pakke.highlights.map((punkt) => (
          <li key={punkt}>{punkt}</li>
        ))}
        {pakke.roadmap.map((punkt) => (
          <li className="soon" key={punkt}>
            {punkt} <span className="tiny">(kommer)</span>
          </li>
        ))}
      </ul>
      <Link
        className={`btn ${pakke.recommended ? 'btn-primary' : 'btn-secondary'} btn-block`}
        to={kjopsvei}
      >
        {pakke.priceNok === 0 ? 'Start gratis' : `${registrert ? 'Kjøp' : 'Velg'} ${pakke.name}`}
      </Link>
    </article>
  );
}

/**
 * Tabellen som svarer på det kortene ikke gjør: hva den ene pakken har som den
 * andre ikke har.
 *
 * Radene kommer fra funksjonsmatrisen, ikke fra en liste skrevet ved siden av
 * den. Flyttes bordplasseringen en dag ned til Standard, flytter haken seg med
 * – uten at noen må huske at det også står en tabell her.
 *
 * Gjestetaket står øverst som sin egen rad. Det er ikke en funksjon man har
 * eller ikke har, men et tall – og det tallet er grunnen til at de fleste
 * oppgraderer.
 *
 * Det som er med i alle pakkene, står samlet under gjestetaket: først det
 * matrisen gir gratis, så verktøyene som ikke er i matrisen i det hele tatt
 * (`I_ALLE_PAKKENE`). Under dem kommer det som koster noe.
 */
function Sammenligning({ pakker }: { pakker: PlanDefinition[] | null }) {
  if (!pakker) return null;
  const gratis = FUNKSJONER.filter((funksjon) => requiredPlanFor(funksjon) === 'free');
  const betalte = FUNKSJONER.filter((funksjon) => requiredPlanFor(funksjon) !== 'free');
  return (
    <div className="table-wrap prisside-tabell">
      <table className="data">
        <thead>
          <tr>
            <th scope="col">Hva som inngår</th>
            {pakker.map((pakke) => (
              <th scope="col" key={pakke.id} className="prisside-kolonne">
                {pakke.name}
                {/* Tallet, ikke ordet: «Gratis» står allerede over, og to like
                    ord under hverandre leses som en feil. */}
                <span className="prisside-tabellpris">{formatNok(pakke.priceNok)}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">Antall gjester</th>
            {pakker.map((pakke) => (
              <td className="prisside-kolonne" key={pakke.id}>
                {pakke.guestLimit === null ? 'Uten tak' : `Inntil ${pakke.guestLimit}`}
              </td>
            ))}
          </tr>
          {gratis.map((funksjon) => (
            <Rad
              key={funksjon}
              navn={NAVN_PAA_FUNKSJON[funksjon]}
              pakker={pakker}
              med={(pakke) => planIncludes(pakke.id, funksjon)}
            />
          ))}
          {I_ALLE_PAKKENE.map((navn) => (
            <Rad key={navn} navn={navn} pakker={pakker} med={() => true} />
          ))}
          {betalte.map((funksjon) => (
            <Rad
              key={funksjon}
              navn={NAVN_PAA_FUNKSJON[funksjon]}
              pakker={pakker}
              med={(pakke) => planIncludes(pakke.id, funksjon)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Rad({
  navn,
  pakker,
  med,
}: {
  navn: string;
  pakker: PlanDefinition[];
  med: (pakke: PlanDefinition) => boolean;
}) {
  return (
    <tr>
      <th scope="row">{navn}</th>
      {pakker.map((pakke) => (
        /* Haken er tegnet, ikke skrevet: «✓» finnes både som tegn og
           som emoji, og hvilken enheten velger, avhenger av fontene den
           har. Navnet ligger på selve tegningen og ikke i en skjult
           tekst ved siden av: en skjermleser leser cella sammen med
           raden og kolonnen den står i, så «Budsjett, Standard, med» er
           hele setningen. */
        <td className="prisside-kolonne" key={pakke.id}>
          <Hake med={med(pakke)} />
        </td>
      ))}
    </tr>
  );
}

function Hake({ med }: { med: boolean }) {
  return (
    <svg
      className={`prisside-hake${med ? ' er-med' : ''}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      role="img"
      aria-label={med ? 'Med' : 'Ikke med'}
      focusable="false"
    >
      {med ? <path d="M5 12.5 10 17.5 19 7" /> : <path d="M7 12h10" />}
    </svg>
  );
}
